import { useTranslation } from "react-i18next";

import {
  IFeeBreakdown,
  IFeeBreakdownStage,
} from "../../../../setup/api/services/contract/models";
import {
  getFeeBreakdownAmount,
  ShippingFeeBreakdownState,
} from "./shippingFeeHelpers";

import styles from "./deliveryFeeModal.module.scss";

interface Props {
  state: ShippingFeeBreakdownState;
  feeBreakdown: IFeeBreakdown;
}

interface StageConfig {
  stage: 1 | 2 | 3;
  titleKey: string;
  defaultDescKey: string;
  emptyDescKey: string;
  isDone: boolean;
  isFinalStage?: boolean;
  amount: number | null;
  stageData: IFeeBreakdownStage | null;
  showBreakdown: boolean;
}

const VanIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    <path d="M1 7h13v10H1z" />
    <path d="M14 10h4l4 4v3h-8z" />
    <circle cx="6" cy="18" r="2" />
    <circle cx="18" cy="18" r="2" />
  </svg>
);

const PercentIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    <line x1="5" y1="19" x2="19" y2="5" />
    <circle cx="7.5" cy="7.5" r="2" />
    <circle cx="16.5" cy="16.5" r="2" />
  </svg>
);

const FeeTimeline = ({ state, feeBreakdown }: Props) => {
  const { t } = useTranslation();

  const formatAmount = (amount: number) =>
    `${amount.toFixed(2)} ${t("lbl_saudi_currency")}`;

  const estimatedOriginalAmount =
    (feeBreakdown?.estimated as number) / (1 + (feeBreakdown?.feeMargin ?? 0));
  const stages: StageConfig[] = [
    {
      stage: 1,
      titleKey: "lbl_shipping_fee_stage_estimated_title",
      defaultDescKey: "lbl_shipping_fee_stage_estimated_desc",
      emptyDescKey: "lbl_shipping_fee_stage_estimated_desc",
      isDone: true,
      amount: getFeeBreakdownAmount(feeBreakdown.estimated),
      stageData: {
        amount: feeBreakdown?.estimated ?? 0,
        actualShippingCost: estimatedOriginalAmount,
        waffyProfit: (feeBreakdown?.estimated ?? 0) - estimatedOriginalAmount,
      },
      showBreakdown: true,
    },
    {
      stage: 2,
      titleKey: "lbl_shipping_fee_stage_charged_title",
      defaultDescKey: "lbl_shipping_fee_stage_charged_desc",
      emptyDescKey: "lbl_shipping_fee_stage_charged_empty_desc",
      isDone: state !== "no-destination",
      amount: getFeeBreakdownAmount(feeBreakdown.charged),
      stageData: {
        amount: feeBreakdown?.charged ?? 0,
        actualShippingCost: feeBreakdown?.originalPrice ?? 0,
        waffyProfit:
          (feeBreakdown?.charged ?? 0) - (feeBreakdown?.originalPrice ?? 0),
      },
      showBreakdown: true,
    },
    {
      stage: 3,
      titleKey: "lbl_shipping_fee_stage_settled_title",
      defaultDescKey: "lbl_shipping_fee_stage_settled_desc",
      emptyDescKey: "lbl_shipping_fee_stage_settled_empty_desc",
      isDone: state === "final",
      isFinalStage: true,
      amount: getFeeBreakdownAmount(feeBreakdown.settled),
      stageData: {
        amount: feeBreakdown?.settled ?? 0,
        actualShippingCost: feeBreakdown?.settledOriginalPrice ?? 0,
        waffyProfit:
          (feeBreakdown?.settled ?? 0) -
          (feeBreakdown?.settledOriginalPrice ?? 0),
      },
      showBreakdown: false,
    },
  ];

  console.log("stages:", stages);
  return (
    <div className={styles.feeTimeline} data-state={state}>
      {stages.map((stage) => (
        <div
          key={stage.stage}
          className={styles.feeStage}
          data-stage={stage.stage}>
          <div className={styles.feeStageContent}>
            <div className={styles.feeStageTitle}>
              {t(stage.titleKey)}
              {stage.isDone ? (
                <span className={styles.feeStageStatusDone}>
                  {stage.isFinalStage
                    ? t("lbl_shipping_fee_status_final")
                    : t("lbl_shipping_fee_status_done")}
                </span>
              ) : (
                <span className={styles.feeStageStatusPending}>
                  {t("lbl_shipping_fee_status_pending")}
                </span>
              )}
            </div>
            <div className={styles.feeStageDesc}>
              <span>
                {stage.isDone ? t(stage.defaultDescKey) : t(stage.emptyDescKey)}
              </span>
            </div>

            {stage.showBreakdown && stage.isDone && (
              <div className={styles.feeStageBreakdown}>
                <div className={styles.feeStageBreakdownItem}>
                  <span className={styles.feeStageBreakdownItemLabel}>
                    <VanIcon />
                    {t("lbl_fee_actual_cost")}
                  </span>
                  <span className={styles.feeStageBreakdownItemValue} dir="ltr">
                    {stage.stageData?.actualShippingCost != null
                      ? formatAmount(stage.stageData.actualShippingCost)
                      : "—"}
                  </span>
                </div>
                <div className={styles.feeStageBreakdownItem}>
                  <span className={styles.feeStageBreakdownItemLabel}>
                    <PercentIcon />
                    {t("lbl_shipping_fee_waffy_profit")}
                  </span>
                  <span
                    className={styles.feeStageBreakdownItemValueWaffy}
                    dir="ltr">
                    {stage.stageData?.waffyProfit != null
                      ? formatAmount(stage.stageData.waffyProfit)
                      : "—"}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className={styles.feeStageAmount}>
            {stage.amount != null ? (
              <span
                className={
                  stage.isFinalStage && stage.isDone
                    ? styles.feeStageAmountValueFinal
                    : styles.feeStageAmountValue
                }
                dir="ltr">
                {formatAmount(stage.amount)}
              </span>
            ) : (
              <span className={styles.feeStageAmountEmpty}>—</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default FeeTimeline;
